import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
} from '@pantry/ui';
import { useContributeTranslation, type Product } from '@pantry/api-client';
import { SUPPORTED_LANGUAGES, type LanguageCode } from '@pantry/shared';
import { toast } from 'sonner';
import { logger } from '../../lib/logger';
import { LanguageIcon, GlobeAltIcon, CheckIcon } from '@heroicons/react/24/outline';

interface TranslationContributionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: Product;
  preferredLanguage: LanguageCode;
  currentName: string;
}

/**
 * Dialog for contributing a translation for a product name.
 * Allows users to add a name in their preferred language when it's missing from OFF.
 */
export function TranslationContributionDialog({
  open,
  onOpenChange,
  product,
  preferredLanguage,
  currentName,
}: TranslationContributionDialogProps) {
  const { t } = useLingui();
  const [translatedName, setTranslatedName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const contributeTranslation = useContributeTranslation();

  const languageName =
    SUPPORTED_LANGUAGES.find((l) => l.code === preferredLanguage)?.name || preferredLanguage;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedName = translatedName.trim();
    if (!trimmedName) {
      setError(t`Please enter a translation`);
      return;
    }

    try {
      await contributeTranslation.mutateAsync({
        productId: product.id,
        language: preferredLanguage,
        name: trimmedName,
      });
      toast.success(t`Translation added for ${languageName}`);
      setTranslatedName('');
      onOpenChange(false);
    } catch (err) {
      logger.error('Failed to contribute translation', err, {
        productId: product.id,
        language: preferredLanguage,
      });
      setError(err instanceof Error ? err.message : t`Failed to save translation`);
    }
  };

  const handleClose = () => {
    setTranslatedName('');
    setError(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <LanguageIcon className="h-4 w-4 text-primary" />
            </div>
            <Trans>Add Translation</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>
              Help improve product names by adding a translation in your preferred language.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="rounded-xl bg-destructive/10 p-3 text-sm text-destructive animate-in">
              {error}
            </div>
          )}

          {/* Current name display */}
          <div className="rounded-xl bg-muted/50 p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <GlobeAltIcon className="h-4 w-4" />
              <Trans>Current name</Trans>
            </div>
            <p className="font-medium text-foreground">{currentName}</p>
            {product.brand && (
              <p className="text-sm text-muted-foreground">
                <Trans>by {product.brand}</Trans>
              </p>
            )}
          </div>

          {/* Translation input */}
          <div className="space-y-2">
            <Label htmlFor="translation" className="flex items-center gap-2">
              <span>
                <Trans>Name in {languageName}</Trans>
              </span>
            </Label>
            <Input
              id="translation"
              type="text"
              value={translatedName}
              onChange={(e) => setTranslatedName(e.target.value)}
              placeholder={t`Enter the product name in ${languageName}`}
              maxLength={500}
              className="rounded-xl"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              <Trans>Your translation will be used for products in your preferred language.</Trans>
            </p>
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={handleClose} className="rounded-xl">
              <Trans>Cancel</Trans>
            </Button>
            <Button
              type="submit"
              disabled={contributeTranslation.isPending || !translatedName.trim()}
              className="rounded-xl"
            >
              {contributeTranslation.isPending ? (
                <>
                  <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  <Trans>Saving...</Trans>
                </>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4 mr-2" />
                  <Trans>Save Translation</Trans>
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
